/**
 * Mission planning — the route workbench.
 *
 * A full-bleed map of the AOR with the draft route on it; a plan/waypoint panel
 * on the left, a route-intelligence panel on the right, a tool palette at the
 * bottom-centre, and an elevation profile along the bottom. Everything is scoped
 * to the AOR the operator is in — the globe is only ever the Command entry.
 *
 * The one distinction the predecessor never drew: a *draft* is what somebody
 * intends; an *uploaded* mission is what the airframe holds and will fly in AUTO.
 * This screen shows both, labels which is which, and writes `uploaded` only from
 * the vehicle's acknowledgement.
 *
 * Honest about terrain: ground track, per-leg distance/bearing, ETA and AGL come
 * straight from the route and are always real. Ground elevation, MSL and terrain
 * clearance need the DEM — they light up when 3D terrain is enabled (Command →
 * Layers) and read "—" otherwise, rather than showing a fabricated number.
 */

import type maplibregl from 'maplibre-gl';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { critiquePlan } from '@/api/ai';
import { parseMissionFile } from '@/api/missions';
import { aorById, lodForZoom, type Aor } from '@/map/aors';
import { MapCanvas, useMapController } from '@/map/MapCanvas';
import { MapContextMenu, type MenuBuilder, type MenuItem } from '@/map/MapContextMenu';
import { DRAFT_POINT_LAYER, commandColor, commandLabel } from '@/map/useMissionLayer';
import { distanceM, pointInZone, rectanglePoints, routeViolations } from '@/map/zoneGeometry';
import {
  moveCorner,
  polygonClick,
  resizeCircleTo,
  translateZone,
  type HandleRole,
} from '@/map/zoneEdit';
import { useZoneEditLayer, ZONE_HANDLE_LAYER } from '@/map/useZoneEditLayer';
import { rerouteAround } from '@/map/pathfinding';
import {
  PATTERN_LABEL,
  SPLIT_LABEL,
  PatternError,
  divideForFleet,
  generatePattern,
  type PatternKind,
  type SplitKind,
} from '@/map/searchPatterns';
import { useDroneMarkers } from '@/map/useDroneMarkers';
import { useMissionLayer } from '@/map/useMissionLayer';
import { useZoneLayer } from '@/map/useZoneLayer';
import { useOverlayLayer } from '@/map/useOverlayLayer';
import { useThreatLayer } from '@/map/useThreatLayer';
import { useAttackLayer, AttackHoverLabel } from '@/map/useAttackLayer';
import { useExternalTrackLayer, ExternalTrackHoverLabel } from '@/map/useExternalTrackLayer';
import { useSimDrag } from '@/map/useSimDrag';
import { useThreatStore } from '@/stores/threatStore';
import { targetingActions } from '@/stores/targetingStore';
import CopilotDock from '@/features/copilot/CopilotDock';
import AttackDock from '@/features/planning/AttackDock';
import { FencePanel } from '@/features/planning/FencePanel';
import { CarrierPanel } from '@/features/planning/CarrierPanel';
import { noteTutorialPattern } from '@/features/tutorial/tutorialStore';
import { useCarrierLayer } from '@/map/useCarrierLayer';
import { teamColorFor } from '@/features/fleet/FleetRail';
import { removeSimulator, spawnSimulators } from '@/api/sim';
import { useCurrentUser, useHasRole } from '@/stores/authStore';
import {
  useDroneIdentity,
  useDroneIds,
  useDroneSelection,
  usePrimarySelectedId,
  useSelectedDroneIds,
  useDroneStore,
} from '@/stores/droneStore';
import { useMapStore, type MapCamera } from '@/stores/mapStore';
import {
  haversineM,
  missionActions,
  useActivePlan,
  useDirtyDrones,
  useDraftMission,
  useDroneIdsWithMissions,
  useMissionBusy,
  useMissionPlans,
  useMissionStore,
  useUploadedMission,
  useZones,
  type Zone,
} from '@/stores/missionStore';
import { MavCmd, type DroneId, type MavCmdValue, type Waypoint } from '@/types/domain';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { useViewport } from '@/mobile/useViewport';
import { useDockLayout, usePlanDock, usePlanDocks } from '@/features/planning/planDocks';

const CRUISE_MS = 12;
const CLEARANCE_BUFFER_M = 30;
/** Nominal still-air range of the reference airframe, for the endurance estimate. */
const NOMINAL_RANGE_M = 22_000;

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';

interface Tool {
  label: string;
  cmd: MavCmdValue;
}
const TOOLS: Tool[] = [
  { label: 'TAKEOFF', cmd: MavCmd.NavTakeoff },
  { label: 'WP', cmd: MavCmd.NavWaypoint },
  { label: 'LOITER', cmd: MavCmd.NavLoiterUnlim },
  { label: 'ROI', cmd: MavCmd.DoSetRoi },
  { label: 'RTL', cmd: MavCmd.NavReturnToLaunch },
  { label: 'LAND', cmd: MavCmd.NavLand },
];

function fmtDist(m: number): string {
  return m < 1000 ? `${m.toFixed(0)} m` : `${(m / 1000).toFixed(2)} km`;
}

function fmtDur(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = Math.PI / 180;
  const y = Math.sin((lon2 - lon1) * toRad) * Math.cos(lat2 * toRad);
  const x =
    Math.cos(lat1 * toRad) * Math.sin(lat2 * toRad) -
    Math.sin(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.cos((lon2 - lon1) * toRad);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Short type name shown on a waypoint row and the elevation legend. */
function wpType(command: number): string {
  switch (command) {
    case MavCmd.NavTakeoff:
      return 'TAKEOFF';
    case MavCmd.NavWaypoint:
      return 'WP';
    case MavCmd.NavLoiterUnlim:
    case MavCmd.NavLoiterTurns:
    case MavCmd.NavLoiterTime:
      return 'LOITER';
    case MavCmd.DoSetRoi:
      return 'ROI';
    case MavCmd.NavReturnToLaunch:
      return 'RTL';
    case MavCmd.NavLand:
      return 'LAND';
    default:
      return commandLabel(command);
  }
}

function download(name: string, mime: string, body: string): void {
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Route analysis ───────────────────────────────────────────────────────────

interface Leg {
  index: number;
  wp: Waypoint;
  legM: number | null;
  bearing: number | null;
  cumM: number;
  aglM: number;
  groundM: number | null;
  mslM: number | null;
  clearanceM: number | null;
}

interface Analysis {
  legs: Leg[];
  totalM: number;
  hasTerrain: boolean;
  minClearance: number | null;
  minClearanceLeg: number | null;
}

/**
 * Build the per-leg table and terrain relationship for a draft. Ground is
 * sampled from the live DEM via queryTerrainElevation; when terrain is not
 * loaded it returns null and the terrain-relative fields stay null.
 */
function analyze(draft: Waypoint[], groundAt: (lat: number, lon: number) => number | null): Analysis {
  let cum = 0;
  let previous: Waypoint | null = null;
  let hasTerrain = false;
  let minClearance: number | null = null;
  let minClearanceLeg: number | null = null;

  const legs: Leg[] = draft.map((wp, index) => {
    let legM: number | null = null;
    let bearing: number | null = null;
    if (previous && !(wp.lat === 0 && wp.lon === 0) && !(previous.lat === 0 && previous.lon === 0)) {
      legM = haversineM(previous.lat, previous.lon, wp.lat, wp.lon);
      bearing = bearingDeg(previous.lat, previous.lon, wp.lat, wp.lon);
      cum += legM;
    }
    const ground = wp.lat === 0 && wp.lon === 0 ? null : groundAt(wp.lat, wp.lon);
    if (ground !== null) hasTerrain = true;
    const msl = ground !== null ? ground + wp.alt : null;
    const clearance = ground !== null ? wp.alt : null; // AGL is clearance at the point itself
    if (clearance !== null && (minClearance === null || clearance < minClearance)) {
      minClearance = clearance;
      minClearanceLeg = index;
    }
    if (!(wp.lat === 0 && wp.lon === 0)) previous = wp;
    return {
      index,
      wp,
      legM,
      bearing,
      cumM: cum,
      aglM: wp.alt,
      groundM: ground,
      mslM: msl,
      clearanceM: clearance,
    };
  });

  return { legs, totalM: cum, hasTerrain, minClearance, minClearanceLeg };
}

// ── Real / sim scope ─────────────────────────────────────────────────────────

/** Amber is the SIM colour everywhere it appears (matches the fleet SIM badge). */
const CAUTION = 'var(--caution)';

export type FleetMode = 'real' | 'sim';

/**
 * Vehicle types the simulator can spawn — mirrors VEHICLE_SPECS in
 * api/app/vehicles/sim.py. The server validates the type, so a drift here fails
 * loudly at spawn rather than silently flying the wrong model.
 */
const SIM_TYPES = ['quad', 'plane', 'rover', 'boat'] as const;

/** Radius of a zone dropped from the map context menu. Editable in intent, but
 *  a sane default beats forcing a number before the operator sees the circle. */
const ZONE_DEFAULT_RADIUS_M = 500;

/** The shapes a zone can be drawn as. A rectangle is stored as a polygon — it
 *  is only a different way of asking for four corners. */
type ZoneDrawShape = 'circle' | 'rect' | 'polygon';
type ZonePoint = { lat: number; lon: number };
interface ZoneDraw {
  kind: 'keep-in' | 'keep-out';
  shape: ZoneDrawShape;
  points: ZonePoint[];
}

function zoneName(kind: 'keep-in' | 'keep-out'): string {
  return kind === 'keep-in' ? 'Keep-in' : 'Keep-out';
}

/** What to click next, so the map is never a mystery mid-draw. */
function zoneHint(draw: ZoneDraw): string {
  if (draw.shape === 'circle') {
    return draw.points.length === 0 ? 'Click the centre' : 'Click the edge to set the radius';
  }
  if (draw.shape === 'rect') {
    return draw.points.length === 0 ? 'Click one corner' : 'Click the opposite corner';
  }
  return draw.points.length < 3
    ? `Click each corner, ${3 - draw.points.length} more at least`
    : `${draw.points.length} corners. Click corner 1 or double-click to close`;
}

/** One shared empty route. `?? []` mints a new array every render, which churns
 *  the identity every useMemo keyed on the draft depends on. */
const NO_DRAFT: Waypoint[] = [];

function sameIds(a: DroneId[], b: DroneId[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * The roster split into real and simulated ids, sampled on a slow interval
 * rather than the telemetry path. Membership and the simulated flag change
 * rarely, so a 1.5 s sweep keeps both lists stable without re-rendering the
 * panel every time a position fix lands. Same idea as features/fleet/aorScope.
 */
function usePlanningRoster(): { realIds: DroneId[]; simIds: DroneId[] } {
  const [roster, setRoster] = useState<{ realIds: DroneId[]; simIds: DroneId[] }>({
    realIds: [],
    simIds: [],
  });

  useEffect(() => {
    const compute = () => {
      const realIds: DroneId[] = [];
      const simIds: DroneId[] = [];
      for (const drone of useDroneStore.getState().drones.values()) {
        (drone.simulated ? simIds : realIds).push(drone.droneId);
      }
      realIds.sort();
      simIds.sort();
      setRoster((prev) =>
        sameIds(prev.realIds, realIds) && sameIds(prev.simIds, simIds)
          ? prev
          : { realIds, simIds },
      );
    };
    compute();
    const timer = window.setInterval(compute, 1500);
    return () => window.clearInterval(timer);
  }, []);

  return roster;
}

function FleetModeTabs({
  mode,
  realCount,
  simCount,
  onChange,
}: {
  mode: FleetMode;
  realCount: number;
  simCount: number;
  onChange: (mode: FleetMode) => void;
}) {
  const tab = (id: FleetMode, label: string, count: number) => {
    const active = mode === id;
    const activeBg = id === 'sim' ? CAUTION : 'rgba(255,255,255,0.92)';
    return (
      <button
        key={id}
        type="button"
        onClick={() => onChange(id)}
        aria-pressed={active}
        className="flex flex-1 items-center justify-center gap-1.5 rounded-[10px] py-[7px] font-mono text-[10px] font-bold tracking-wide transition-colors"
        style={{
          background: active ? activeBg : 'transparent',
          color: active ? '#0c0c0d' : 'rgba(244,244,245,0.6)',
        }}
      >
        {label}
        <span
          className="tnum rounded-full px-1.5 text-[9px]"
          style={{
            background: active ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.08)',
            color: active ? '#0c0c0d' : 'rgba(244,244,245,0.55)',
          }}
        >
          {count}
        </span>
      </button>
    );
  };
  return (
    <div className="well flex gap-1 rounded-[13px] p-1">
      {tab('real', 'REAL', realCount)}
      {tab('sim', 'SIM', simCount)}
    </div>
  );
}

/** One selectable aircraft in the planning roster. Marks in-plan and dirty state. */
function RosterRow({
  droneId,
  editing,
  selected,
  inPlan,
  dirty,
  canManage,
  onSelect,
  onToggle,
  onRemove,
}: {
  droneId: DroneId;
  editing: boolean;
  selected: boolean;
  inPlan: boolean;
  dirty: boolean;
  canManage: boolean;
  onSelect: (additive: boolean) => void;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const identity = useDroneIdentity(droneId);
  if (!identity) return null;
  const stale = identity.state === 'stale' || identity.state === 'disconnected';
  const accent = identity.simulated ? CAUTION : 'var(--friend)';

  return (
    <div
      className="flex items-center gap-2 rounded-[11px] border px-2.5 py-1.5"
      style={{
        borderColor: editing || selected ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.06)',
        background: editing ? 'rgba(255,255,255,0.10)' : selected ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.025)',
      }}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        aria-label={`Select ${identity.callsign} to apply a mission to`}
        className="size-[14px] flex-none accent-[color:var(--friend)]"
      />
      <button
        type="button"
        // Modifier-click adds to the selection for an apply-to-many; a plain
        // click picks this one aircraft to edit and replaces the selection.
        onClick={(event) => onSelect(event.metaKey || event.ctrlKey || event.shiftKey)}
        aria-pressed={selected}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <span className="size-2 flex-none rounded-full" style={{ background: accent, opacity: stale ? 0.4 : 1 }} />
        <span className="tnum truncate font-mono text-[11.5px] font-semibold">{identity.callsign}</span>
        {inPlan ? (
          <span
            className="rounded-full px-1.5 py-[1px] font-mono text-[7.5px] font-bold"
            style={{
              color: dirty ? WARN : OK,
              background: `color-mix(in oklch, ${dirty ? WARN : OK} 16%, transparent)`,
            }}
          >
            {dirty ? 'DIRTY' : 'ROUTE'}
          </span>
        ) : null}
        {stale ? (
          <span className="ml-auto font-mono text-[8px] text-white/40">{identity.state.toUpperCase()}</span>
        ) : null}
      </button>
      {canManage && identity.simulated ? (
        <button
          type="button"
          onClick={onRemove}
          title={`Remove ${identity.callsign}`}
          aria-label={`Remove ${identity.callsign}`}
          className="flex-none rounded-full px-1.5 font-mono text-[12px] leading-none text-white/35 hover:text-[color:var(--danger)]"
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

/**
 * The mode-scoped aircraft picker: pick which aircraft to plan for, and — in SIM
 * mode, for admins — spawn or remove simulators without leaving the screen. Real
 * links are never spawned or removed here; they come and go through Command.
 */
function PlanningRoster({
  mode,
  ids,
  editingDrone,
  planDroneIds,
  dirtyDrones,
  onSelect,
  onMessage,
}: {
  mode: FleetMode;
  ids: DroneId[];
  editingDrone: DroneId | null;
  planDroneIds: DroneId[];
  dirtyDrones: DroneId[];
  onSelect: (id: DroneId) => void;
  onMessage: (text: string, warn?: boolean) => void;
}) {
  const canManage = useHasRole('admin');
  const aor = aorById(useWorkspaceStore((state) => state.aor));
  const controller = useMapController();
  const { select, toggleSelect, setSelection, clearSelection } = useDroneSelection();
  const selectedIds = useSelectedDroneIds();
  const [vtype, setVtype] = useState<(typeof SIM_TYPES)[number]>('quad');
  const [spawning, setSpawning] = useState(false);
  // Optional exact coordinates. Empty means "spawn where I am looking"; filled
  // means "put it exactly here" — the realistic case of placing an aircraft at
  // a known grid reference rather than eyeballing the map.
  const [spawnLat, setSpawnLat] = useState('');
  const [spawnLon, setSpawnLon] = useState('');

  const planSet = useMemo(() => new Set(planDroneIds), [planDroneIds]);
  const dirtySet = useMemo(() => new Set(dirtyDrones), [dirtyDrones]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  // How many of the aircraft in *this* list are selected — the "N SELECTED"
  // counter must not include a drone hidden under the other fleet tab.
  const selectedHere = ids.filter((id) => selectedSet.has(id));

  const spawn = async () => {
    setSpawning(true);
    // Exact coordinates win when both are given and valid — placing an aircraft
    // at a known grid reference. Otherwise spawn where the operator is looking
    // (the map centre), falling back to the AOR centre before the map exists.
    const typedLat = spawnLat.trim() === '' ? null : Number(spawnLat);
    const typedLon = spawnLon.trim() === '' ? null : Number(spawnLon);
    const coordsGiven = typedLat !== null || typedLon !== null;
    if (coordsGiven) {
      const valid =
        typedLat !== null &&
        typedLon !== null &&
        Number.isFinite(typedLat) &&
        Number.isFinite(typedLon) &&
        Math.abs(typedLat) <= 90 &&
        Math.abs(typedLon) <= 180;
      if (!valid) {
        setSpawning(false);
        onMessage('Enter a valid lat (-90..90) and lon (-180..180), or clear both.', true);
        return;
      }
    }
    const centre = controller.raw()?.getCenter();
    const lat = typedLat ?? (centre ? centre.lat : aor.center[1]);
    const lon = typedLon ?? (centre ? centre.lng : aor.center[0]);
    const result = await spawnSimulators({ vehicleType: vtype, lat, lon });
    setSpawning(false);
    if (!result.ok) {
      onMessage(result.error.message, true);
      return;
    }
    const added = result.data.added[0];
    if (added) {
      onSelect(added);
      // Fly to the exact drop so a coordinate spawn is never off-screen.
      controller.flyTo(lat, lon);
    }
    onMessage(
      coordsGiven
        ? `Spawned ${vtype} at ${lat.toFixed(5)}, ${lon.toFixed(5)}`
        : `Spawned ${result.data.added.length} ${vtype} simulator — planning for ${added ?? 'it'}`,
    );
  };

  const remove = async (id: DroneId) => {
    const result = await removeSimulator(id);
    if (!result.ok) {
      onMessage(result.error.message, true);
      return;
    }
    if (id === editingDrone) clearSelection();
    onMessage(`Removed simulator ${id}`);
  };

  const [clearingSims, setClearingSims] = useState(false);
  const removeAll = async () => {
    setClearingSims(true);
    let removed = 0;
    // One at a time — despawning the whole bench in parallel hammers the sim
    // manager and the roster flickers as each removal lands out of order.
    for (const id of ids) {
      const result = await removeSimulator(id);
      if (result.ok) removed += 1;
    }
    setClearingSims(false);
    clearSelection();
    onMessage(`Removed ${removed} simulator${removed === 1 ? '' : 's'}`);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="label-mono text-[8.5px]">{mode === 'sim' ? 'SIMULATORS' : 'REAL AIRCRAFT'}</span>
        <span className="tnum font-mono text-[9px] text-white/40">{ids.length}</span>
      </div>

      {ids.length ? (
        <div className="flex items-center gap-2 font-mono text-[8.5px] text-white/45">
          {selectedHere.length ? (
            <span style={{ color: 'var(--friend)' }}>{selectedHere.length} SELECTED</span>
          ) : (
            <span>ctrl-click for many</span>
          )}
          <button
            type="button"
            onClick={() => setSelection(ids)}
            className="ml-auto rounded-full px-2 py-[2px] hover:bg-white/10 hover:text-white"
          >
            ALL
          </button>
          {selectedHere.length ? (
            <button
              type="button"
              onClick={clearSelection}
              className="rounded-full px-2 py-[2px] hover:bg-white/10 hover:text-white"
            >
              CLEAR
            </button>
          ) : null}
        </div>
      ) : null}

      {mode === 'sim' && canManage ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex gap-1.5">
            <input
              value={spawnLat}
              onChange={(event) => setSpawnLat(event.target.value)}
              inputMode="decimal"
              placeholder="lat (optional)"
              aria-label="Spawn latitude"
              className="well w-1/2 rounded-[8px] px-2 py-1 font-mono text-[10px] outline-none"
            />
            <input
              value={spawnLon}
              onChange={(event) => setSpawnLon(event.target.value)}
              inputMode="decimal"
              placeholder="lon (optional)"
              aria-label="Spawn longitude"
              className="well w-1/2 rounded-[8px] px-2 py-1 font-mono text-[10px] outline-none"
            />
          </div>
          <div className="flex gap-1.5">
          <select
            value={vtype}
            onChange={(event) => setVtype(event.target.value as (typeof SIM_TYPES)[number])}
            aria-label="Simulator vehicle type"
            className="well rounded-[10px] px-2 py-1.5 font-mono text-[10px] outline-none"
          >
            {SIM_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={spawning}
            onClick={() => void spawn()}
            className="flex-1 rounded-[10px] border px-3 py-1.5 font-mono text-[9px] font-bold tracking-wide disabled:opacity-40"
            style={{
              color: '#0c0c0d',
              background: CAUTION,
              borderColor: CAUTION,
            }}
          >
            {spawning ? 'SPAWNING…' : '+ SPAWN SIM'}
          </button>
          {ids.length ? (
            <button
              type="button"
              disabled={clearingSims}
              onClick={() => void removeAll()}
              title="Despawn every simulator"
              className="rounded-[10px] border border-white/15 px-3 py-1.5 font-mono text-[9px] font-bold tracking-wide text-white/60 hover:text-[color:var(--danger)] disabled:opacity-40"
            >
              {clearingSims ? 'REMOVING…' : `REMOVE ALL ${ids.length}`}
            </button>
          ) : null}
          </div>
        </div>
      ) : null}

      {ids.length ? (
        <div className="flex flex-col gap-1">
          {ids.map((id) => (
            <RosterRow
              key={id}
              droneId={id}
              editing={id === editingDrone}
              selected={selectedSet.has(id)}
              inPlan={planSet.has(id)}
              dirty={dirtySet.has(id)}
              canManage={canManage}
              onSelect={(additive) => (additive ? toggleSelect(id) : select(id))}
              onToggle={() => toggleSelect(id)}
              onRemove={() => void remove(id)}
            />
          ))}
        </div>
      ) : (
        <p className="px-1 py-1 text-[10.5px] leading-relaxed text-white/40">
          {mode === 'sim'
            ? canManage
              ? 'No simulators running. Spawn one above to plan and test against it.'
              : 'No simulators running. An admin can spawn one from here.'
            : 'No real aircraft connected. Bring a link up from Command, or switch to SIM to plan against a simulator.'}
        </p>
      )}
    </div>
  );
}

// ── Left panel ───────────────────────────────────────────────────────────────

function PlanPanel({
  editingDrone,
  analysis,
  selectedWp,
  onSelectWp,
  onUpload,
  onMessage,
  message,
}: {
  editingDrone: DroneId | null;
  analysis: Analysis;
  selectedWp: number | null;
  onSelectWp: (index: number) => void;
  onUpload: () => void;
  onMessage: (text: string, warn?: boolean) => void;
  message: { text: string; warn: boolean } | null;
}) {
  const plans = useMissionPlans();
  const activePlan = useActivePlan();
  const user = useCurrentUser();
  const dirty = useDirtyDrones();
  const busy = useMissionBusy();
  const draft = useDraftMission(editingDrone) ?? NO_DRAFT;
  const uploaded = useUploadedMission(editingDrone) ?? [];
  const planDroneIds = useDroneIdsWithMissions();
  const { select, clearSelection } = useDroneSelection();
  const selectedIds = useSelectedDroneIds();
  const { realIds, simIds } = usePlanningRoster();
  const zones = useZones();

  // A zone that cannot stop an upload is decoration. This is the deterrence:
  // the route is checked against every zone and the transfer is refused while
  // it breaches one. Most aircraft carry no fence until somebody pushes one,
  // so for them this is the last point at which anything can say no.
  const breaches = useMemo(() => routeViolations(draft, zones), [draft, zones]);

  const [mode, setMode] = useState<FleetMode>('real');
  const modePickedByOperator = useRef(false);

  // Until the operator picks a tab, show whichever fleet actually has aircraft.
  // The default fleet is simulators (SIM_DRONES), so a hardcoded REAL tab reads
  // empty while every sim sits unseen under SIM — which looked like "no drones
  // on Planning" even though Command showed them mixed together.
  useEffect(() => {
    if (modePickedByOperator.current) return;
    if (realIds.length === 0 && simIds.length > 0) setMode('sim');
    else if (realIds.length > 0) setMode('real');
  }, [realIds, simIds]);

  // Keep the tab in step with the selected aircraft: selecting a sim (here or on
  // Command) flips to SIM, so the panel can never claim REAL while a simulator
  // is the upload target.
  useEffect(() => {
    if (editingDrone && simIds.includes(editingDrone)) setMode('sim');
    else if (editingDrone && realIds.includes(editingDrone)) setMode('real');
  }, [editingDrone, simIds, realIds]);

  const changeMode = (next: FleetMode) => {
    modePickedByOperator.current = true;
    setMode(next);
    // Don't carry a real aircraft into the SIM tab or a sim into REAL: clear it
    // so the operator re-picks inside the mode they mean to command.
    if (editingDrone && simIds.includes(editingDrone) !== (next === 'sim')) clearSelection();
  };

  const rosterIds = mode === 'sim' ? simIds : realIds;

  const isDirty = editingDrone !== null && dirty.includes(editingDrone);
  const isOwner = activePlan !== null && user !== null && activePlan.ownerId === user.userId;

  return (
    <aside className="plan-primary glass pointer-events-auto absolute left-[14px] top-[86px] bottom-[190px] flex w-[var(--plan-l-w)] flex-col overflow-hidden rounded-[20px]">
      <div className="flex flex-none flex-col gap-2.5 px-4 pt-3.5 pb-2.5">
        <div className="flex items-center gap-2">
          <span className="label-mono text-[10px]">PLAN</span>
          <span
            className="rounded-full px-2 py-[2px] font-mono text-[8px] font-bold tracking-wide"
            style={{
              background: isDirty ? WARN : OK,
              color: isDirty ? '#1c1503' : '#0c2214',
            }}
          >
            {isDirty ? 'DIRTY — NOT UPLOADED' : uploaded.length ? 'IN SYNC — ON AIRCRAFT' : 'DRAFT'}
          </span>
        </div>

        <select
          value={activePlan?.planId ?? ''}
          onChange={(event) => void missionActions.selectPlan(event.target.value)}
          aria-label="Active plan"
          className="well rounded-[12px] px-3 py-2 font-mono text-[12px] outline-none"
        >
          {activePlan === null ? <option value="">No plan open</option> : null}
          {plans.map((plan) => (
            <option key={plan.planId} value={plan.planId}>
              {plan.name}
            </option>
          ))}
        </select>

        <PlanManageRow activePlan={activePlan} isOwner={isOwner} onMessage={onMessage} />

        {activePlan ? (
          <div className="font-mono text-[10.5px] text-white/50">
            {isOwner ? 'You own this plan' : `Owned by ${activePlan.ownerUsername}`}
            {activePlan.collaboratorIds.length
              ? ` · ${activePlan.collaboratorIds.length} collaborator${activePlan.collaboratorIds.length === 1 ? '' : 's'}`
              : ''}
          </div>
        ) : null}

        <FleetModeTabs
          mode={mode}
          realCount={realIds.length}
          simCount={simIds.length}
          onChange={changeMode}
        />

        {mode === 'sim' ? (
          <div
            className="flex items-start gap-2 rounded-[12px] border px-3 py-2"
            style={{
              borderColor: `color-mix(in oklch, ${CAUTION} 55%, transparent)`,
              background: `color-mix(in oklch, ${CAUTION} 10%, transparent)`,
            }}
          >
            <span className="mt-[1px] text-[11px]" style={{ color: CAUTION }}>
              ◆
            </span>
            <p className="font-mono text-[9.5px] leading-relaxed" style={{ color: CAUTION }}>
              SIMULATION — these are simulated aircraft. Uploads and commands here never reach real
              hardware.
            </p>
          </div>
        ) : null}

      </div>

      <div className="scroll-thin flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-2.5 pb-2.5">
        <PlanningRoster
          mode={mode}
          ids={rosterIds}
          editingDrone={editingDrone}
          planDroneIds={planDroneIds}
          dirtyDrones={dirty}
          onSelect={select}
          onMessage={onMessage}
        />

        <div className="flex items-center gap-3 px-1 font-mono text-[8.5px] font-semibold tracking-wide text-white/45">
          <span className="flex items-center gap-1.5">
            <span className="h-0 w-4 border-t-2 border-dashed border-white/60" /> DRAFT
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-[2px] w-4 bg-white/85" /> ON AIRCRAFT
          </span>
        </div>

        <div className="flex flex-col gap-1">
        {!editingDrone ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">
            Pick an aircraft above to plan a route for it. Each aircraft in a plan carries its own
            route.
          </p>
        ) : draft.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">
            No waypoints yet. Pick a tool below and click the map, or right-click for the full menu.
          </p>
        ) : (
          analysis.legs.map((leg) => {
            const selected = selectedWp === leg.index;
            const warn = leg.clearanceM !== null && leg.clearanceM < CLEARANCE_BUFFER_M;
            return (
              <div
                key={leg.index}
                className="relative rounded-[13px] border"
                style={{
                  borderColor: selected ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.05)',
                  background: selected ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.025)',
                }}
              >
                <button
                  type="button"
                  onClick={() => onSelectWp(leg.index)}
                  className="flex w-full flex-col gap-1 px-2.5 py-2 pr-8 text-left"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="flex size-5 flex-none items-center justify-center rounded-full font-mono text-[9.5px] font-bold"
                      style={{
                        background: warn ? `color-mix(in oklch, ${DANGER} 35%, transparent)` : 'rgba(255,255,255,0.12)',
                        color: warn ? DANGER : '#f4f4f5',
                      }}
                    >
                      {leg.index + 1}
                    </span>
                    <span
                      className="size-2 flex-none rounded-full"
                      style={{ background: commandColor(leg.wp.command) }}
                      aria-hidden
                    />
                    <span
                      className="font-mono text-[11.5px] font-semibold"
                      style={{ color: commandColor(leg.wp.command) }}
                    >
                      {wpType(leg.wp.command)}
                    </span>
                    {warn ? (
                      <span
                        className="rounded-full border px-1.5 py-[1px] font-mono text-[8px] font-bold"
                        style={{ color: DANGER, borderColor: `color-mix(in oklch, ${DANGER} 55%, transparent)` }}
                      >
                        TERRAIN {leg.clearanceM?.toFixed(0)} m
                      </span>
                    ) : null}
                    <span className="ml-auto font-mono text-[10px] text-white/50">
                      {leg.legM === null ? '—' : `${fmtDist(leg.legM)} · ${leg.bearing?.toFixed(0).padStart(3, '0')}°`}
                    </span>
                  </div>
                  <div className="flex gap-2.5 pl-7 font-mono text-[10px] text-white/50">
                    <span>AGL {leg.aglM.toFixed(0)} m</span>
                    <span>MSL {leg.mslM === null ? '—' : `${leg.mslM.toFixed(0)} m`}</span>
                    <span>GND {leg.groundM === null ? '—' : `${leg.groundM.toFixed(0)} m`}</span>
                  </div>
                </button>
                {editingDrone ? (
                  <button
                    type="button"
                    aria-label={`Delete waypoint ${leg.index + 1}`}
                    title="Delete waypoint"
                    onClick={() => {
                      missionActions.removeWaypoint(editingDrone, leg.index);
                      if (selectedWp === leg.index) onSelectWp(leg.index);
                    }}
                    className="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded-full font-mono text-[11px] text-white/45 hover:bg-white/10 hover:text-white"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            );
          })
        )}
        </div>
      </div>

      <div className="glass-pill flex flex-none flex-wrap gap-1.5 border-t border-white/10 px-3 py-2.5">
        <PlanButton primary onClick={() => void missionActions.savePlan().then((ok) => onMessage(ok ? 'Plan saved' : 'Save failed', !ok))}>
          SAVE
        </PlanButton>
        <PlanButton
          disabled={busy || !draft.length || !editingDrone || breaches.length > 0}
          onClick={onUpload}
        >
          {breaches.length ? '⚠ ZONE BREACH' : 'UPLOAD ⇡'}
        </PlanButton>
        {selectedIds.length > 1 ? (
          <PlanButton
            disabled={busy || !draft.length || !editingDrone || breaches.length > 0}
            onClick={() => {
              if (!editingDrone) return;
              void missionActions.applyToDrones(editingDrone, selectedIds).then((outcomes) => {
                const ok = outcomes.filter((o) => o.ok).length;
                const failed = outcomes.length - ok;
                onMessage(
                  failed
                    ? `Applied to ${ok}/${outcomes.length} — ${failed} refused`
                    : `Applied this route to all ${ok} selected aircraft`,
                  failed > 0,
                );
              });
            }}
          >
            APPLY TO {selectedIds.length} ⇡
          </PlanButton>
        ) : null}
        <PlanButton
          disabled={busy || !editingDrone}
          onClick={() => editingDrone && void missionActions.downloadFromDrone(editingDrone).then((r) => onMessage(r.message, !r.ok))}
        >
          READ BACK
        </PlanButton>
        <PlanButton
          disabled={!uploaded.length || !editingDrone}
          onClick={() => {
            if (!editingDrone) return;
            missionActions.adoptUploaded(editingDrone);
            onMessage('Adopted the aircraft route into the draft — review before re-upload', true);
          }}
        >
          ADOPT AC ROUTE
        </PlanButton>
        <PlanButton
          danger
          disabled={!draft.length || !editingDrone}
          onClick={() => {
            if (!editingDrone) return;
            missionActions.clearDraft(editingDrone);
            onMessage('Draft cleared — the uploaded mission is untouched', true);
          }}
        >
          CLEAR
        </PlanButton>
      </div>

      {message ? (
        <div className="flex-none px-4 pb-2.5 font-mono text-[10px]" style={{ color: message.warn ? WARN : OK }}>
          {message.text}
        </div>
      ) : null}
    </aside>
  );
}

/**
 * Create / rename / delete / join / leave for the active plan. Owner-only
 * actions hide rather than fail; the server enforces the same rule.
 */
function PlanManageRow({
  activePlan,
  isOwner,
  onMessage,
}: {
  activePlan: ReturnType<typeof useActivePlan>;
  isOwner: boolean;
  onMessage: (text: string, warn?: boolean) => void;
}) {
  const user = useCurrentUser();
  const [mode, setMode] = useState<'idle' | 'new' | 'rename'>('idle');
  const [name, setName] = useState('');
  const [delArmed, setDelArmed] = useState(false);

  // The server lets the owner OR an admin rename and delete (is_administrable_by).
  // Gating these on ownership alone hid them from an admin looking at someone
  // else's plan, so an admin's own delete of another operator's plan silently
  // had no button — which read as "the plan won't delete".
  const canAdminister =
    activePlan !== null && (isOwner || user?.role === 'admin');
  const isCollaborator =
    activePlan !== null && user !== null && activePlan.collaboratorIds.includes(user.userId);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (mode === 'new') {
      void missionActions.createPlan(trimmed).then(() => onMessage(`Plan "${trimmed}" created`));
    } else if (mode === 'rename' && activePlan) {
      void missionActions
        .renamePlan(activePlan.planId, trimmed)
        .then(() => onMessage(`Renamed to "${trimmed}"`));
    }
    setMode('idle');
    setName('');
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap gap-1.5">
        <MiniButton
          onClick={() => {
            setMode((m) => (m === 'new' ? 'idle' : 'new'));
            setName('');
          }}
        >
          + NEW
        </MiniButton>
        {activePlan && canAdminister ? (
          <>
            <MiniButton
              onClick={() => {
                setMode((m) => (m === 'rename' ? 'idle' : 'rename'));
                setName(activePlan.name);
              }}
            >
              RENAME
            </MiniButton>
            <MiniButton
              danger
              onClick={() => {
                if (!delArmed) {
                  setDelArmed(true);
                  onMessage('Deleting a plan removes every route in it — press again to confirm', true);
                  return;
                }
                setDelArmed(false);
                void missionActions
                  .removePlan(activePlan.planId)
                  .then(() => onMessage('Plan deleted'));
              }}
            >
              {delArmed ? 'CONFIRM DELETE' : 'DELETE'}
            </MiniButton>
          </>
        ) : null}
        {activePlan && !canAdminister && !isCollaborator ? (
          <MiniButton
            onClick={() =>
              void missionActions
                .joinPlan(activePlan.planId)
                .then(() => onMessage('Joined as collaborator — your edits save to this plan'))
            }
          >
            JOIN AS COLLABORATOR
          </MiniButton>
        ) : null}
        {activePlan && !canAdminister && isCollaborator ? (
          <MiniButton
            onClick={() =>
              void missionActions
                .leavePlan(activePlan.planId)
                .then(() => onMessage('Left the plan'))
            }
          >
            LEAVE
          </MiniButton>
        ) : null}
      </div>
      {mode !== 'idle' ? (
        <div className="flex gap-1.5">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
            placeholder={mode === 'new' ? 'new plan name' : 'new name'}
            className="well h-7 min-w-0 flex-1 rounded-[99px] px-3 text-[11px] outline-none"
          />
          <button
            type="button"
            disabled={!name.trim()}
            onClick={submit}
            className="rounded-full bg-white/90 px-3 font-mono text-[9px] font-bold text-[#0c0c0d] disabled:opacity-40"
          >
            {mode === 'new' ? 'CREATE' : 'SAVE'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function MiniButton({
  children,
  onClick,
  danger,
}: {
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border px-2.5 py-[4px] font-mono text-[8.5px] font-bold"
      style={{
        color: danger ? DANGER : 'rgba(244,244,245,0.6)',
        borderColor: danger
          ? `color-mix(in oklch, ${DANGER} 50%, transparent)`
          : 'rgba(255,255,255,0.14)',
      }}
    >
      {children}
    </button>
  );
}

function PlanButton({
  children,
  onClick,
  disabled,
  primary,
  danger,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-full border px-3 py-[7px] font-mono text-[9px] font-bold tracking-wide disabled:opacity-40"
      style={{
        background: primary ? 'rgba(255,255,255,0.9)' : danger ? 'transparent' : 'rgba(255,255,255,0.05)',
        color: primary ? '#0c0c0d' : danger ? DANGER : 'rgba(244,244,245,0.75)',
        borderColor: primary
          ? 'rgba(255,255,255,0.9)'
          : danger
            ? `color-mix(in oklch, ${DANGER} 50%, transparent)`
            : 'rgba(255,255,255,0.14)',
      }}
    >
      {children}
    </button>
  );
}

/**
 * Generate a search pattern over the area in view.
 *
 * Laying a lawnmower by hand is a dozen clicks that all have to be evenly
 * spaced, and the spacing is the part that matters — it decides whether the
 * sensor actually saw the ground between two passes. So it is typed, not
 * eyeballed.
 *
 * Centred on the map, because that is the area the operator is looking at, and
 * sized from the radius they give. Replaces the draft rather than appending:
 * half of an old route followed by a survey is neither.
 */
function PatternDock({
  editingDrone,
  onMessage,
}: {
  editingDrone: DroneId | null;
  onMessage: (text: string, warn?: boolean) => void;
}) {
  const controller = useMapController();
  const selectedIds = useSelectedDroneIds();
  const [kind, setKind] = useState<PatternKind>('grid');
  const [split, setSplit] = useState<SplitKind>('strips');
  const [radius, setRadius] = useState('600');
  const [spacing, setSpacing] = useState('120');
  const [alt, setAlt] = useState('60');

  // Everything selected shares the area; one aircraft just gets all of it.
  const fleet = selectedIds.length > 1 ? selectedIds : editingDrone ? [editingDrone] : [];

  const run = () => {
    if (fleet.length === 0) {
      onMessage('Pick an aircraft to plan the pattern for', true);
      return;
    }
    const centre = controller.raw()?.getCenter();
    if (!centre) return;
    const spec = {
      kind,
      center: { lat: centre.lat, lon: centre.lng },
      radiusM: Number(radius),
      spacingM: Number(spacing),
      altM: Number(alt),
    };
    try {
      if (fleet.length > 1) {
        // One area divided, not the same route copied: each aircraft flies its
        // own slice, and the slices together are exactly the single-aircraft
        // route — no seam, no strip flown twice.
        const slices = divideForFleet(spec, fleet.length, split);
        fleet.forEach((id, index) => missionActions.setDraft(id, slices[index] ?? []));
        noteTutorialPattern();
        onMessage(
          `${PATTERN_LABEL[kind]} split ${SPLIT_LABEL[split].toLowerCase()} across ${fleet.length} aircraft`,
        );
        return;
      }
      const route = generatePattern(spec);
      missionActions.setDraft(fleet[0]!, route);
      noteTutorialPattern();
      onMessage(`${PATTERN_LABEL[kind]} — ${route.length} waypoints`);
    } catch (error) {
      // A refusal explains itself: the spacing is wider than the area, or the
      // route is bigger than an autopilot will hold.
      onMessage(
        error instanceof PatternError ? error.message : 'That pattern could not be generated',
        true,
      );
    }
  };

  return (
    <div>
      <div className="label-mono mb-2 text-[9px]">SEARCH PATTERN</div>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {(Object.keys(PATTERN_LABEL) as PatternKind[]).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setKind(option)}
            className="rounded-full border px-2.5 py-1 font-mono text-[8.5px] font-bold tracking-wide"
            style={{
              color: kind === option ? '#0c0c0d' : 'rgba(244,244,245,0.7)',
              background: kind === option ? OK : 'transparent',
              borderColor: kind === option ? OK : 'rgba(255,255,255,0.14)',
            }}
          >
            {PATTERN_LABEL[option]}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] text-white/45">
        <label className="flex items-center gap-1">
          r
          <input
            value={radius}
            onChange={(event) => setRadius(event.target.value)}
            inputMode="numeric"
            aria-label="Pattern radius in metres"
            className="well w-[58px] rounded-[8px] px-1.5 py-0.5 text-right text-[10px] outline-none"
          />
          m
        </label>
        <label className="flex items-center gap-1">
          spacing
          <input
            value={spacing}
            onChange={(event) => setSpacing(event.target.value)}
            inputMode="numeric"
            aria-label="Pattern spacing in metres"
            className="well w-[54px] rounded-[8px] px-1.5 py-0.5 text-right text-[10px] outline-none"
          />
          m
        </label>
        <label className="flex items-center gap-1">
          alt
          <input
            value={alt}
            onChange={(event) => setAlt(event.target.value)}
            inputMode="numeric"
            aria-label="Pattern altitude in metres"
            className="well w-[48px] rounded-[8px] px-1.5 py-0.5 text-right text-[10px] outline-none"
          />
          m
        </label>
        <button
          type="button"
          disabled={fleet.length === 0}
          onClick={run}
          className="ml-auto rounded-full border px-3 py-1 font-mono text-[9px] font-bold tracking-wide disabled:opacity-40"
          style={{ color: '#0c0c0d', background: OK, borderColor: OK }}
        >
          {fleet.length > 1 ? `GENERATE ×${fleet.length}` : 'GENERATE'}
        </button>
      </div>
      {fleet.length > 1 ? (
        <div className="mt-1.5 flex items-center gap-1.5">
          <span className="font-mono text-[9px] text-white/45">SPLIT BY</span>
          {(Object.keys(SPLIT_LABEL) as SplitKind[]).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setSplit(option)}
              className="rounded-full border px-2 py-[2px] font-mono text-[8.5px] font-bold"
              style={{
                color: split === option ? '#0c0c0d' : 'rgba(244,244,245,0.7)',
                background: split === option ? OK : 'transparent',
                borderColor: split === option ? OK : 'rgba(255,255,255,0.14)',
              }}
            >
              {SPLIT_LABEL[option]}
            </button>
          ))}
        </div>
      ) : null}
      <p className="mt-1.5 text-[9.5px] leading-relaxed text-white/40">
        Centred on the map view. Replaces the current draft.
        {fleet.length > 1
          ? ' The area is divided between the selected aircraft — each flies its own slice.'
          : ''}
      </p>
    </div>
  );
}

// ── Right panel: route intelligence ──────────────────────────────────────────

function RoutePanel({
  editingDrone,
  analysis,
  selectedWp,
  onMessage,
  zoneDraw,
  onZoneDraw,
  selectedZoneId,
  onSelectZone,
  open,
}: {
  editingDrone: DroneId | null;
  analysis: Analysis;
  selectedWp: number | null;
  onMessage: (text: string, warn?: boolean) => void;
  zoneDraw: ZoneDraw | null;
  onZoneDraw: (draw: ZoneDraw | null) => void;
  selectedZoneId: string | null;
  onSelectZone: (zoneId: string | null) => void;
  /** Tablet only: the panel is a drawer and this says whether it is out. */
  open: boolean;
}) {
  const draft = useDraftMission(editingDrone) ?? NO_DRAFT;
  const zones = useZones();
  const [critique, setCritique] = useState<string | null>(null);
  const [critiqueBusy, setCritiqueBusy] = useState(false);

  const runCritique = () => {
    if (!editingDrone || !draft.length) return;
    setCritiqueBusy(true);
    setCritique(null);
    void critiquePlan(editingDrone, draft)
      .then((result) => setCritique(result.ok ? result.data.critique : result.error.message))
      .finally(() => setCritiqueBusy(false));
  };

  const etaS = analysis.totalM / CRUISE_MS;
  const consumed = Math.min(99, (analysis.totalM / NOMINAL_RANGE_M) * 100);
  const reserve = Math.max(0, 100 - consumed);
  const selLeg = selectedWp !== null ? analysis.legs[selectedWp] : null;

  const stats: Array<{ k: string; v: string; color?: string }> = [
    { k: 'GROUND TRACK', v: fmtDist(analysis.totalM) },
    { k: 'EST TIME', v: `${fmtDur(etaS)} @ ${CRUISE_MS} m/s` },
    {
      k: 'SELECTED LEG',
      v: selLeg && selLeg.legM !== null ? `${fmtDist(selLeg.legM)} · ${selLeg.bearing?.toFixed(0)}°` : '—',
    },
    {
      k: 'MIN CLEARANCE',
      v: analysis.hasTerrain && analysis.minClearance !== null ? `${analysis.minClearance.toFixed(0)} m` : 'terrain off',
      color: analysis.hasTerrain && analysis.minClearance !== null && analysis.minClearance < CLEARANCE_BUFFER_M ? DANGER : undefined,
    },
    { k: 'BATTERY EST', v: `${consumed.toFixed(0)}% of pack` },
    { k: 'RESERVE AT RTL', v: `${reserve.toFixed(0)}% · ${reserve > 15 ? 'OK' : 'LOW'}`, color: reserve > 15 ? OK : DANGER },
  ];

  const belowBuffer =
    analysis.hasTerrain && analysis.minClearance !== null && analysis.minClearance < CLEARANCE_BUFFER_M;

  // The only deterrence a zone has on this system: catch the route before it is
  // uploaded. Legs are sampled, so a leg crossing a keep-out between two clean
  // waypoints is caught, not just a waypoint dropped inside one.
  const violations = useMemo(() => routeViolations(draft, zones), [draft, zones]);

  const exportPlan = () => {
    const items = draft.map((wp, i) => ({
      AMSLAltAboveTerrain: null,
      Altitude: wp.alt,
      AltitudeMode: 1,
      autoContinue: wp.autocontinue,
      command: wp.command,
      doJumpId: i + 1,
      frame: wp.frame,
      params: [wp.param1, wp.param2, wp.param3, wp.param4, wp.lat, wp.lon, wp.alt],
      type: 'SimpleItem',
    }));
    const home = draft[0] ?? { lat: 0, lon: 0, alt: 0 };
    const plan = {
      fileType: 'Plan',
      version: 1,
      groundStation: 'Parallax',
      mission: {
        version: 2,
        firmwareType: 12,
        vehicleType: 2,
        cruiseSpeed: CRUISE_MS,
        hoverSpeed: 5,
        plannedHomePosition: [home.lat, home.lon, home.alt],
        items,
      },
    };
    download(`${editingDrone ?? 'route'}.plan`, 'application/json', JSON.stringify(plan, null, 2));
    onMessage(`Exported ${draft.length} items as QGC .plan`);
  };

  const exportWaypoints = () => {
    const lines = ['QGC WPL 110'];
    draft.forEach((wp, i) => {
      lines.push(
        [i, i === 0 ? 1 : 0, wp.frame, wp.command, wp.param1, wp.param2, wp.param3, wp.param4, wp.lat, wp.lon, wp.alt, wp.autocontinue ? 1 : 0].join('\t'),
      );
    });
    download(`${editingDrone ?? 'route'}.waypoints`, 'text/plain', lines.join('\n'));
    onMessage(`Exported ${draft.length} items as MAVLink .waypoints`);
  };

  return (
    <aside
      data-open={open ? '' : undefined}
      className="plan-secondary glass scroll-thin pointer-events-auto absolute right-[14px] top-[86px] bottom-[190px] flex w-[var(--plan-r-w)] flex-col overflow-y-auto rounded-[20px]"
    >
      <div className="flex flex-col gap-3.5 p-3.5">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="label-mono text-[9px]">ROUTE INTELLIGENCE</span>
            <button
              type="button"
              disabled={critiqueBusy || !draft.length}
              onClick={runCritique}
              className="ml-auto rounded-full border border-white/15 bg-white/5 px-3 py-[5px] font-mono text-[8.5px] font-bold text-white/70 disabled:opacity-40"
            >
              {critiqueBusy ? 'REVIEWING…' : '⚚ AI CRITIQUE'}
            </button>
          </div>
          {critique ? (
            <div className="mb-2 rounded-[12px] border border-white/10 bg-black/25 px-3 py-2.5">
              <p className="whitespace-pre-wrap text-[10.5px] leading-relaxed">{critique}</p>
              <p className="mt-1 label-mono text-[7.5px] text-white/40">
                AI-GENERATED — ADVISORY, THE ROUTE IS YOURS
              </p>
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-1.5">
            {stats.map((stat) => (
              <div key={stat.k} className="rounded-[12px] border border-white/8 bg-white/5 px-3 py-2">
                <div className="label-mono text-[8.5px]">{stat.k}</div>
                <div className="mt-0.5 truncate font-mono text-[12.5px]" style={{ color: stat.color }}>
                  {stat.v}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-col gap-1.5">
            <div className="flex justify-between font-mono text-[10px] text-white/50">
              <span>ENDURANCE MARGIN</span>
              <span style={{ color: reserve > 15 ? OK : DANGER }}>{reserve > 15 ? `+${(reserve - 15).toFixed(0)}%` : `${(reserve - 15).toFixed(0)}%`}</span>
            </div>
            <div className="flex h-1.5 overflow-hidden rounded-full bg-black/35">
              <span style={{ width: `${consumed}%`, background: 'rgba(255,255,255,0.75)' }} />
              <span style={{ width: `${reserve}%`, background: OK }} />
            </div>
            <div className="font-mono text-[9.5px] text-white/40">
              {consumed.toFixed(0)}% consumed by plan @ {CRUISE_MS} m/s cruise · reserve floor 15% · rough estimate
            </div>
          </div>
        </div>

        {belowBuffer ? (
          <div
            className="flex gap-2.5 rounded-[14px] border bg-white/[0.04] px-3 py-2.5"
            style={{ borderColor: `color-mix(in oklch, ${DANGER} 50%, transparent)` }}
          >
            <span className="mt-0.5 text-[13px]" style={{ color: DANGER }}>
              ⚠
            </span>
            <div className="text-[11px] leading-relaxed">
              <span className="label-mono text-[9px]" style={{ color: DANGER }}>
                TERRAIN CLEARANCE
              </span>
              <br />
              Leg {(analysis.minClearanceLeg ?? 0) + 1} passes {analysis.minClearance?.toFixed(0)} m above
              ground — below the {CLEARANCE_BUFFER_M} m buffer. Raise the waypoint or reroute.
            </div>
          </div>
        ) : null}

        {violations.length ? (
          <div
            className="rounded-[12px] border px-3 py-2.5"
            style={{
              borderColor: `color-mix(in oklch, ${DANGER} 45%, transparent)`,
              background: `color-mix(in oklch, ${DANGER} 10%, transparent)`,
            }}
          >
            <div className="label-mono mb-1 text-[9px]" style={{ color: DANGER }}>
              ⚠ ROUTE BREACHES {violations.length} ZONE{violations.length === 1 ? '' : 'S'}
            </div>
            <div className="flex flex-col gap-1">
              {violations.map((v) => (
                <div key={`${v.zoneId}-${v.index}`} className="text-[11px] text-white/75">
                  <span style={{ color: DANGER }}>{v.zoneName}</span>{' '}
                  {v.kind === 'keep-out'
                    ? v.onLeg
                      ? `— leg ${v.index + 1}→${v.index + 2} passes through it`
                      : `— waypoint ${v.index + 1} sits inside it`
                    : v.onLeg
                      ? `— leg ${v.index + 1}→${v.index + 2} leaves it`
                      : `— waypoint ${v.index + 1} is outside it`}
                </div>
              ))}
            </div>
            <p className="mt-1.5 text-[10px] text-white/45">
              Upload is blocked while the route breaches a zone. Move the waypoints, remove the
              zone, or route around it.
            </p>
            {editingDrone ? (
              <button
                type="button"
                onClick={() => {
                  const result = rerouteAround(draft, zones);
                  if (result.reason) {
                    onMessage(result.reason, true);
                    return;
                  }
                  if (!result.changed) {
                    onMessage('No way around that keeps the same waypoints', true);
                    return;
                  }
                  missionActions.setDraft(editingDrone, result.waypoints);
                  const added = result.waypoints.length - draft.length;
                  onMessage(`Routed around — ${added} waypoint${added === 1 ? '' : 's'} added`);
                }}
                className="mt-2 rounded-full border px-3 py-[6px] font-mono text-[9px] font-bold tracking-wide"
                style={{
                  color: '#0c0c0d',
                  background: OK,
                  borderColor: OK,
                }}
              >
                ROUTE AROUND ⤳
              </button>
            ) : null}
          </div>
        ) : null}

        <PatternDock editingDrone={editingDrone} onMessage={onMessage} />

        <div>
          <div className="label-mono mb-2 text-[9px]">DRAW A ZONE</div>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {(
              [
                ['keep-out', 'circle', 'KEEP-OUT ○'],
                ['keep-out', 'rect', 'KEEP-OUT ▭'],
                ['keep-out', 'polygon', 'KEEP-OUT ⬠'],
                ['keep-in', 'circle', 'KEEP-IN ○'],
                ['keep-in', 'rect', 'KEEP-IN ▭'],
                ['keep-in', 'polygon', 'KEEP-IN ⬠'],
              ] as Array<['keep-in' | 'keep-out', ZoneDrawShape, string]>
            ).map(([kind, shape, label]) => {
              const active = zoneDraw?.kind === kind && zoneDraw?.shape === shape;
              const accent = kind === 'keep-out' ? DANGER : OK;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => onZoneDraw(active ? null : { kind, shape, points: [] })}
                  className="rounded-full border px-2.5 py-1 font-mono text-[8.5px] font-bold tracking-wide"
                  style={{
                    color: active ? '#0c0c0d' : accent,
                    background: active ? accent : 'transparent',
                    borderColor: `color-mix(in oklch, ${accent} 45%, transparent)`,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="label-mono mb-2 flex items-center gap-2 text-[9px]">
            <span>ZONES</span>
            {zones.length ? (
              <button
                type="button"
                onClick={() => missionActions.clearZones()}
                className="ml-auto rounded-full px-2 py-[1px] text-white/45 hover:bg-white/10 hover:text-white"
              >
                CLEAR
              </button>
            ) : null}
          </div>
          {zones.length ? (
            <div className="flex flex-col gap-1.5">
              {zones.map((zone) => (
                <div
                  key={zone.zoneId}
                  className="flex items-center gap-2.5 rounded-[12px] border bg-white/5 px-3 py-2 text-[11.5px]"
                  style={{
                    borderColor:
                      zone.zoneId === selectedZoneId ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.08)',
                  }}
                >
                  <span
                    className="size-3 flex-none"
                    style={
                      zone.kind === 'keep-in'
                        ? { border: '2px dashed rgba(255,255,255,0.6)', borderRadius: 4 }
                        : { border: `2px solid ${DANGER}`, borderRadius: 99 }
                    }
                  />
                  <span className="min-w-0 truncate">
                    {zone.kind === 'keep-in' ? 'KEEP-IN' : 'KEEP-OUT'}
                    {zone.shape.type === 'polygon' ? ` · ${zone.shape.points.length}-pt` : ''}
                  </span>
                  <button
                    type="button"
                    aria-pressed={zone.zoneId === selectedZoneId}
                    aria-label={`Edit ${zone.name} zone on the map`}
                    onClick={() => onSelectZone(zone.zoneId === selectedZoneId ? null : zone.zoneId)}
                    className="flex-none rounded-full border border-white/15 px-2 py-[1px] font-mono text-[8.5px] font-bold text-white/60 hover:bg-white/10 hover:text-white"
                  >
                    {zone.zoneId === selectedZoneId ? 'DONE' : 'EDIT'}
                  </button>
                  {zone.shape.type === 'circle' ? (
                    <label className="ml-auto flex items-center gap-1 font-mono text-[10px] text-white/45">
                      r
                      <input
                        // Keyed on the radius so a drag of the edge handle
                        // shows up here; an uncontrolled field would keep the
                        // number it was first rendered with.
                        key={Math.round(zone.shape.radiusM)}
                        type="number"
                        min={1}
                        step={50}
                        defaultValue={Math.round(zone.shape.radiusM)}
                        aria-label={`${zone.name} radius in metres`}
                        onBlur={(event) => {
                          const next = Number(event.target.value);
                          if (!Number.isFinite(next) || next < 1) return;
                          missionActions.resizeZone(zone.zoneId, next);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') event.currentTarget.blur();
                        }}
                        className="well w-[62px] rounded-[8px] px-1.5 py-0.5 text-right text-[10px] outline-none"
                      />
                      m
                    </label>
                  ) : null}
                  <button
                    type="button"
                    aria-label={`Remove ${zone.name} zone`}
                    title="Remove zone"
                    onClick={() => {
                      if (zone.zoneId === selectedZoneId) onSelectZone(null);
                      missionActions.removeZone(zone.zoneId);
                    }}
                    className="ml-auto flex-none rounded-full px-1.5 font-mono text-[12px] leading-none text-white/35 hover:text-[color:var(--danger)]"
                  >
                    ×
                  </button>
                </div>
              ))}
              {selectedZoneId ? (
                <p className="text-[9.5px] leading-relaxed text-white/45">
                  Drag a numbered corner or the edge handle to reshape it, or drag inside it to move
                  it. It is saved once, when you let go.
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-[10.5px] text-white/40">
              No zones defined. Draw one above, or right-click the map to drop a keep-in or
              keep-out area. Routes and gotos through a zone are refused before they are sent.
            </p>
          )}
        </div>

        <FencePanel onMessage={onMessage} />

        <CarrierPanel editingDrone={editingDrone} onMessage={onMessage} />

        <div>
          <div className="label-mono mb-2 text-[9px]">IMPORT / EXPORT</div>
          <div className="flex flex-wrap gap-1.5">
            <ImportPlanButton editingDrone={editingDrone} onMessage={onMessage} />
            <PlanButton disabled={!draft.length} onClick={exportPlan}>
              EXPORT .PLAN
            </PlanButton>
            <PlanButton disabled={!draft.length} onClick={exportWaypoints}>
              .WAYPOINTS
            </PlanButton>
          </div>
          <p className="mt-1.5 text-[9.5px] leading-relaxed text-white/40">
            QGC-compatible. Only MavCmds the backend supports are offered — the palette can never
            produce an unrepresentable item.
          </p>
        </div>
      </div>
    </aside>
  );
}

function ImportPlanButton({
  editingDrone,
  onMessage,
}: {
  editingDrone: DroneId | null;
  onMessage: (text: string, warn?: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  // The SERVER parses the file: it speaks both QGC .plan and MAVLink
  // .waypoints, and it drops commands the backend cannot fly with a warning
  // naming each one — so an import can never smuggle an unflyable item into
  // the draft. The predecessor's client-side parser silently kept them.
  const onFile = async (file: File) => {
    if (!editingDrone) {
      onMessage('Select an aircraft before importing a plan', true);
      return;
    }
    const result = await parseMissionFile(file.name, await file.text());
    if (!result.ok) {
      onMessage(result.error.message, true);
      return;
    }
    const { waypoints, sourceFormat, warnings } = result.data;
    if (!waypoints.length) {
      onMessage('No importable items in that file', true);
      return;
    }
    missionActions.setDraft(editingDrone, waypoints);
    const warning = warnings.length ? ` · ${warnings.length} warning(s): ${warnings[0]}` : '';
    onMessage(
      `Imported ${waypoints.length} items (${sourceFormat}) into the draft${warning}`,
      warnings.length > 0,
    );
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".plan,.waypoints,.txt,application/json,text/plain"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void onFile(file);
          event.target.value = '';
        }}
      />
      <PlanButton onClick={() => inputRef.current?.click()}>IMPORT .PLAN</PlanButton>
    </>
  );
}

// ── Palette + elevation profile ──────────────────────────────────────────────

function PaletteDock({ tool, onTool }: { tool: MavCmdValue; onTool: (cmd: MavCmdValue) => void }) {
  return (
    <div className="plan-palette glass-pill pointer-events-auto absolute left-1/2 bottom-[190px] z-10 flex -translate-x-1/2 gap-1 rounded-full p-1">
      {TOOLS.map((t) => (
        <button
          key={t.label}
          type="button"
          onClick={() => onTool(t.cmd)}
          className="rounded-full px-3.5 py-2 font-mono text-[9.5px] font-bold tracking-wide"
          style={{
            background: tool === t.cmd ? 'rgba(255,255,255,0.92)' : 'transparent',
            color: tool === t.cmd ? '#0c0c0d' : 'rgba(244,244,245,0.55)',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The live chip while a zone is being drawn: what to click next, how far along,
 * and the way out. Sits above the palette so it never covers the point being
 * clicked.
 */
function ZoneDrawDock({
  draw,
  onDone,
  onCancel,
  onUndo,
}: {
  draw: ZoneDraw;
  onDone: () => void;
  onCancel: () => void;
  onUndo: () => void;
}) {
  const accent = draw.kind === 'keep-out' ? DANGER : OK;
  return (
    <div className="plan-zonedraw glass-pill pointer-events-auto absolute left-1/2 bottom-[244px] z-20 flex max-w-[calc(100vw-16px)] -translate-x-1/2 flex-wrap items-center gap-2 rounded-[18px] px-3 py-1.5">
      <span className="label-mono text-[9px]" style={{ color: accent }}>
        {zoneName(draw.kind).toUpperCase()} · {draw.shape.toUpperCase()}
      </span>
      <span className="font-mono text-[10px] text-white/60">{zoneHint(draw)}</span>
      {draw.points.length ? (
        <button
          type="button"
          onClick={onUndo}
          className="rounded-full px-2 py-1 font-mono text-[9px] text-white/55 hover:bg-white/10 hover:text-white"
        >
          UNDO
        </button>
      ) : null}
      {draw.shape === 'polygon' && draw.points.length >= 3 ? (
        <button
          type="button"
          onClick={onDone}
          className="rounded-full px-2.5 py-1 font-mono text-[9px] font-bold"
          style={{ background: accent, color: '#0c0c0d' }}
        >
          DONE
        </button>
      ) : null}
      <button
        type="button"
        onClick={onCancel}
        className="rounded-full px-2 py-1 font-mono text-[9px] text-white/55 hover:bg-white/10 hover:text-white"
      >
        CANCEL
      </button>
    </div>
  );
}

function ElevationProfile({
  editingDrone,
  analysis,
}: {
  editingDrone: DroneId | null;
  analysis: Analysis;
}) {
  const positional = analysis.legs.filter((l) => !(l.wp.lat === 0 && l.wp.lon === 0));

  const svg = useMemo(() => {
    if (positional.length < 2) return null;
    const total = analysis.totalM || 1;
    // Plot in MSL when the DEM gave us ground; otherwise in AGL (relative),
    // which is always real from the route even with terrain off.
    const useMsl = analysis.hasTerrain;
    const planVals = positional.map((l) => (useMsl && l.mslM !== null ? l.mslM : l.aglM));
    const groundVals = useMsl ? positional.map((l) => l.groundM ?? 0) : [];
    const all = [...planVals, ...groundVals];
    const lo = Math.min(...all) - 20;
    const hi = Math.max(...all) + 20;
    const span = hi - lo || 1;
    const x = (cum: number) => (cum / total) * 600;
    const y = (v: number) => 96 - ((v - lo) / span) * 96;

    const planLine = positional.map((l, i) => `${x(l.cumM).toFixed(1)},${y(planVals[i]!).toFixed(1)}`).join(' ');
    const warnLine = useMsl
      ? positional
          .filter((l) => l.clearanceM !== null && l.clearanceM < CLEARANCE_BUFFER_M)
          .map((l) => `${x(l.cumM).toFixed(1)},${y(l.mslM ?? 0).toFixed(1)}`)
          .join(' ')
      : '';
    const groundLine = useMsl
      ? 'M' + positional.map((l) => `${x(l.cumM).toFixed(1)} ${y(l.groundM ?? 0).toFixed(1)}`).join(' L')
      : '';
    const groundPath = groundLine ? `${groundLine} L600 96 L0 96 Z` : '';
    const dots = positional.map((l, i) => ({
      x: x(l.cumM).toFixed(1),
      y: y(planVals[i]!).toFixed(1),
      warn: l.clearanceM !== null && l.clearanceM < CLEARANCE_BUFFER_M,
    }));
    return { planLine, warnLine, groundPath, dots, useMsl };
  }, [positional, analysis.hasTerrain, analysis.totalM]);

  return (
    <section className="plan-profile glass pointer-events-auto absolute left-[14px] right-[14px] bottom-[14px] flex h-[162px] flex-col overflow-hidden rounded-[18px]">
      <div className="flex flex-none flex-wrap items-center gap-2.5 px-4 pt-2.5 pb-1.5">
        <span className="label-mono text-[8.5px]">
          ELEVATION PROFILE{editingDrone ? ` — ${editingDrone}` : ''}
        </span>
        <span className="flex items-center gap-1.5 font-mono text-[8.5px] text-white/50">
          <span className="h-[2px] w-3.5 bg-white/90" /> PLANNED {svg?.useMsl ? 'MSL' : 'AGL'}
        </span>
        {svg?.useMsl ? (
          <span className="flex items-center gap-1.5 font-mono text-[8.5px] text-white/50">
            <span className="h-2 w-3.5 bg-white/20" /> TERRAIN
          </span>
        ) : null}
        <span className="flex items-center gap-1.5 font-mono text-[8.5px]" style={{ color: DANGER }}>
          <span className="h-[2px] w-3.5" style={{ background: DANGER }} /> &lt; {CLEARANCE_BUFFER_M} m BUFFER
        </span>
        <span className="ml-auto font-mono text-[9.5px] text-white/45">
          {fmtDist(analysis.totalM)} · {positional.length} WP · EST {fmtDur(analysis.totalM / CRUISE_MS)}
        </span>
      </div>
      <div className="min-h-0 flex-1 px-4 pb-2.5 pt-0.5">
        {svg ? (
          <svg viewBox="0 0 600 96" preserveAspectRatio="none" className="block size-full">
            {svg.groundPath ? <path d={svg.groundPath} fill="rgba(255,255,255,0.16)" /> : null}
            <polyline points={svg.planLine} fill="none" stroke="rgba(255,255,255,0.92)" strokeWidth="1.8" />
            {svg.warnLine ? (
              <polyline points={svg.warnLine} fill="none" stroke={DANGER} strokeWidth="2.4" />
            ) : null}
            {svg.dots.map((d, i) => (
              <circle
                key={i}
                cx={d.x}
                cy={d.y}
                r="3"
                fill="#0c0c0d"
                stroke={d.warn ? DANGER : 'rgba(255,255,255,0.9)'}
                strokeWidth="1.8"
              />
            ))}
          </svg>
        ) : (
          <div className="flex size-full items-center justify-center text-[11px] text-white/40">
            Add two or more waypoints to see the elevation profile.
          </div>
        )}
      </div>
    </section>
  );
}

// ── Upload dialog ────────────────────────────────────────────────────────────

function UploadDialog({
  editingDrone,
  simulated,
  analysis,
  onClose,
  onMessage,
}: {
  editingDrone: DroneId;
  simulated: boolean;
  analysis: Analysis;
  onClose: () => void;
  onMessage: (text: string, warn?: boolean) => void;
}) {
  // Memoise the empty-fallback so a null draft yields a stable [] reference --
  // otherwise `?? []` builds a fresh array every render and the counts useMemo
  // below (keyed on `draft`) would recompute on every render.
  const draftValue = useDraftMission(editingDrone);
  const draft = useMemo(() => draftValue ?? [], [draftValue]);
  const busy = useMissionBusy();
  const counts = useMemo(() => {
    const by: Record<string, number> = {};
    draft.forEach((wp) => {
      const t = wpType(wp.command).toLowerCase();
      by[t] = (by[t] ?? 0) + 1;
    });
    return Object.entries(by)
      .map(([k, n]) => `${n} ${k}`)
      .join(' · ');
  }, [draft]);

  const belowBuffer = analysis.hasTerrain && analysis.minClearance !== null && analysis.minClearance < CLEARANCE_BUFFER_M;

  const confirm = () => {
    void missionActions.uploadToDrone(editingDrone).then((r) => {
      onMessage(r.message, !r.ok);
      onClose();
    });
  };

  return (
    <div
      className="pointer-events-auto absolute inset-0 z-40 flex items-center justify-center p-6 phone:p-3"
      style={{ background: 'rgba(6,6,8,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="glass scroll-thin flex max-h-full w-[min(440px,100%)] flex-col gap-4 overflow-y-auto rounded-[22px] p-6 phone:p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-2.5">
          <span className="text-[15px]" style={{ color: WARN }}>
            ⇧
          </span>
          <span className="label-mono text-[12px]">UPLOAD MISSION TO AIRCRAFT</span>
        </div>
        <div
          className="flex items-center gap-2 rounded-[12px] border px-3 py-2"
          style={{
            borderColor: simulated
              ? `color-mix(in oklch, ${CAUTION} 55%, transparent)`
              : `color-mix(in oklch, ${DANGER} 55%, transparent)`,
            background: simulated
              ? `color-mix(in oklch, ${CAUTION} 12%, transparent)`
              : `color-mix(in oklch, ${DANGER} 12%, transparent)`,
          }}
        >
          <span className="label-mono text-[9px]" style={{ color: simulated ? CAUTION : DANGER }}>
            {simulated ? '◆ SIMULATED AIRCRAFT' : '● REAL AIRCRAFT'}
          </span>
          <span className="font-mono text-[10px] text-white/60">
            {simulated ? 'no real hardware is affected' : 'this commands live hardware'}
          </span>
        </div>
        <p className="text-[12.5px] leading-relaxed text-white/75">
          This writes <b className="text-foreground">{draft.length} items</b> to{' '}
          <b className="text-foreground">{editingDrone}</b> and replaces whatever mission it holds.
          The aircraft will fly it when switched to <span className="font-mono">AUTO</span>.
        </p>
        <div className="well flex flex-col gap-1.5 rounded-[14px] px-3.5 py-3 font-mono text-[11px]">
          <Row k="Items" v={counts || '—'} />
          <Row k="Ground track" v={`${fmtDist(analysis.totalM)} · est ${fmtDur(analysis.totalM / CRUISE_MS)}`} />
          <Row
            k="Terrain clearance"
            v={belowBuffer ? '1+ leg below buffer' : analysis.hasTerrain ? 'clear' : 'terrain off'}
            color={belowBuffer ? WARN : undefined}
          />
          <Row k="After upload" v="read back & verify" />
        </div>
        <div className="flex justify-end gap-2">
          <PlanButton onClick={onClose}>CANCEL</PlanButton>
          <PlanButton primary disabled={busy} onClick={confirm}>
            UPLOAD TO {editingDrone} ⇡
          </PlanButton>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, color }: { k: string; v: string; color?: string }) {
  return (
    <div className="flex justify-between gap-2.5">
      <span className="text-white/50">{k}</span>
      <span style={{ color }}>{v}</span>
    </div>
  );
}

// ── Composition ──────────────────────────────────────────────────────────────

function PlanningWorkspace() {
  const controller = useMapController();
  const editingDrone = usePrimarySelectedId();
  const identity = useDroneIdentity(editingDrone ?? '');
  const draft = useDraftMission(editingDrone) ?? NO_DRAFT;
  const terrainOn = useMapStore((state) => state.terrainOn);
  const lod = useMapStore((state) => state.lod);

  const [tool, setTool] = useState<MavCmdValue>(MavCmd.NavWaypoint);
  const [selectedWp, setSelectedWp] = useState<number | null>(null);
  const tier = useViewport();
  const { open: routeOpen, toggle: toggleRoute } = usePlanDock('route');
  const dockLayout = useDockLayout();
  useEffect(() => usePlanDocks.getState().settle(dockLayout), [dockLayout]);

  // Zone drawing. `points` accumulates map clicks; what completes the shape
  // differs per kind — a circle takes centre then edge, a rectangle takes two
  // opposite corners, a polygon takes vertices until the operator says done.
  const [zoneDraw, setZoneDrawState] = useState<ZoneDraw | null>(null);
  const zoneDrawRef = useRef<ZoneDraw | null>(null);
  zoneDrawRef.current = zoneDraw;
  // The ref moves with the state, not a render later, so the second click of
  // a double-click sees the corner the first one placed.
  const setZoneDraw = (next: ZoneDraw | null | ((d: ZoneDraw | null) => ZoneDraw | null)) => {
    const value = typeof next === 'function' ? next(zoneDrawRef.current) : next;
    zoneDrawRef.current = value;
    setZoneDrawState(value);
  };

  // The zone whose corners and body can be dragged. One at a time, and never
  // while a new one is being drawn, so a click always means one thing.
  const [selectedZoneId, setSelectedZoneIdState] = useState<string | null>(null);
  const selectedZoneRef = useRef<string | null>(null);
  selectedZoneRef.current = selectedZoneId;
  const setSelectedZoneId = (zoneId: string | null) => {
    selectedZoneRef.current = zoneId;
    setSelectedZoneIdState(zoneId);
  };
  const startZoneDraw = (draw: ZoneDraw | null) => {
    setSelectedZoneId(null);
    setZoneDraw(draw);
  };
  const zones = useZones();
  const selectedZone = useMemo(
    () => zones.find((zone) => zone.zoneId === selectedZoneId) ?? null,
    [zones, selectedZoneId],
  );
  const zoneEditView = useMemo(
    () => ({ draw: zoneDraw, selected: zoneDraw ? null : selectedZone }),
    [zoneDraw, selectedZone],
  );
  useZoneEditLayer(zoneEditView);

  const addZonePoint = (lat: number, lon: number) => {
    const draw = zoneDrawRef.current;
    if (!draw) return;
    if (draw.shape === 'polygon') {
      const map = controller.raw();
      if (map) {
        const corners = draw.points.map((p) => map.project([p.lon, p.lat]));
        const action = polygonClick(map.project([lon, lat]), corners);
        if (action === 'close') {
          finishPolygon();
          return;
        }
        if (action === 'ignore') return;
      }
    }
    const points = [...draw.points, { lat, lon }];

    if (draw.shape === 'circle' && points.length === 2) {
      const [centre, edge] = points as [ZonePoint, ZonePoint];
      const radiusM = Math.round(distanceM(centre.lat, centre.lon, edge.lat, edge.lon));
      missionActions.addZone({
        name: zoneName(draw.kind),
        kind: draw.kind,
        shape: { type: 'circle', lat: centre.lat, lon: centre.lon, radiusM: Math.max(1, radiusM) },
      });
      onMessage(`${zoneName(draw.kind)} · ${fmtDist(radiusM)} radius`);
      setZoneDraw(null);
      return;
    }

    if (draw.shape === 'rect' && points.length === 2) {
      const [a, b] = points as [ZonePoint, ZonePoint];
      missionActions.addZone({
        name: zoneName(draw.kind),
        kind: draw.kind,
        shape: { type: 'polygon', points: rectanglePoints(a.lat, a.lon, b.lat, b.lon) },
      });
      onMessage(`${zoneName(draw.kind)} · rectangle`);
      setZoneDraw(null);
      return;
    }

    setZoneDraw({ ...draw, points });
  };

  const finishPolygon = () => {
    const draw = zoneDrawRef.current;
    if (!draw || draw.shape !== 'polygon' || draw.points.length < 3) return;
    missionActions.addZone({
      name: zoneName(draw.kind),
      kind: draw.kind,
      shape: { type: 'polygon', points: draw.points },
    });
    onMessage(`${zoneName(draw.kind)} · ${draw.points.length}-point area`);
    setZoneDraw(null);
  };
  const finishPolygonRef = useRef(finishPolygon);
  finishPolygonRef.current = finishPolygon;
  const [uploadOpen, setUploadOpen] = useState(false);
  const [message, setMessage] = useState<{ text: string; warn: boolean } | null>(null);
  const toolRef = useRef(tool);
  toolRef.current = tool;

  const onMessage = (text: string, warn = false) => setMessage({ text, warn });
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useDroneMarkers({ colorFor: teamColorFor });
  useMissionLayer({ showDraft: true });
  useZoneLayer();
  useOverlayLayer();
  useThreatLayer();
  // Mounted last so the attack geometry (target, run-in, impact) sits above the
  // routes and zones it is planned against — the same layering reason the
  // command-target layer is created by the screen rather than a child.
  useAttackLayer();
  // Somebody else's reported contacts. A route planned around an airspace that
  // is missing every ADS-B and TAK contact in it is a route planned against
  // half the picture, so the planner sees the same external tracks the
  // commander does — drawn so neither can mistake one for our own aircraft.
  useExternalTrackLayer();
  // Carrier deploy points and what is aboard each boat, kept current by the
  // executor's socket frames.
  useCarrierLayer();

  // Leaving Planning disarms the place-a-target click mode, and only that.
  //
  // It used to reset the whole store, which dropped the target and its solution
  // on the way out — and Command now draws that geometry, so a target placed
  // here was erased the moment the operator went to the screen that commands
  // the aircraft. The commander saw nothing. What actually had to not survive
  // the trip is the armed click mode, because a click on Command means "fly
  // here", not "aim here". The solution is still retired the instant the target
  // moves or the mode changes (targetingStore), so nothing stale is drawn;
  // clearing the target itself is the operator's own CLEAR in the attack dock.
  useEffect(() => () => targetingActions.cancelPlacement(), []);

  useEffect(() => {
    void missionActions.loadPlans();
    void missionActions.loadUploaded();
    // Zones are deployment-wide: the boundary another operator drew has to be
    // on this screen before anyone plans against it.
    void missionActions.loadZones();
    void useThreatStore.getState().loadThreats();
  }, []);

  useEffect(() => {
    missionActions.setEditingDrone(editingDrone);
  }, [editingDrone]);

  // Publish LOD to the body so the marker-hiding CSS knows this screen's zoom.
  // Command owns document.body.dataset.lod and DELETES it on unmount, so leaving
  // Command for Planning strips the attribute entirely — and
  // `body:not([data-lod='tactical']) .dm-marker { display:none }` then hides
  // every aircraft on the Planning map even though the markers exist and are
  // positioned. This was the real reason drones showed in the roster but never
  // on the Planning map. Planning must publish its own lod while mounted.
  useEffect(() => {
    document.body.dataset.lod = lod;
    return () => {
      delete document.body.dataset.lod;
    };
  }, [lod]);

  // Never open Planning on an empty map.
  //
  // The camera is inherited from Command, which may be looking somewhere the
  // aircraft are not — a different AOR, or a spot the operator had panned to.
  // The markers are created and positioned correctly either way, so the screen
  // is simply aimed elsewhere, and an operator seeing aircraft in the roster
  // and none on the map has no way to tell that is all that is wrong.
  //
  // Runs once, and only when nothing is already in view, so it can never fight
  // an operator who has deliberately panned away.
  const framedRef = useRef(false);
  const droneIds = useDroneIds();
  useEffect(() => {
    if (framedRef.current || droneIds.length === 0) return;
    const map = controller.raw();
    if (!map) return;
    const points: Array<[number, number]> = [];
    for (const drone of useDroneStore.getState().drones.values()) {
      const { lat, lon } = drone.scalars;
      if (typeof lat !== 'number' || typeof lon !== 'number') continue;
      if (lat === 0 && lon === 0) continue;
      points.push([lon, lat]);
    }
    if (points.length === 0) return;
    framedRef.current = true;
    const bounds = map.getBounds();
    if (!points.some(([lon, lat]) => bounds.contains([lon, lat]))) {
      controller.fitPoints(points, 160);
    }
  }, [controller, droneIds]);

  // Drag a waypoint to move it.
  //
  // Hand-rolled on the GL layer rather than a DOM marker per waypoint: a route
  // is already drawn as one source, and a hundred absolutely-positioned markers
  // over a moving map is the thing that makes a planning screen stutter.
  //
  // The map's own drag has to be disabled for the duration or the whole camera
  // pans while the operator thinks they are moving a point.
  useEffect(() => {
    const map = controller.raw();
    if (!map) return undefined;

    let dragging: { droneId: DroneId; index: number } | null = null;

    const onDown = (event: maplibregl.MapMouseEvent) => {
      const target = missionActions.editingDroneId();
      if (!target || zoneDrawRef.current) return;
      const hit = map
        .queryRenderedFeatures(event.point, { layers: [DRAFT_POINT_LAYER] })
        .find((feature) => typeof feature.properties?.seq === 'number');
      if (!hit) return;
      // seq is 1-based on the wire so the labels read naturally.
      dragging = { droneId: target, index: Number(hit.properties.seq) - 1 };
      event.preventDefault();
      map.dragPan.disable();
      map.getCanvas().style.cursor = 'grabbing';
    };

    const onMove = (event: maplibregl.MapMouseEvent) => {
      if (!dragging) return;
      missionActions.moveWaypoint(dragging.droneId, dragging.index, event.lngLat.lat, event.lngLat.lng);
    };

    const onUp = () => {
      if (!dragging) return;
      dragging = null;
      map.dragPan.enable();
      map.getCanvas().style.cursor = '';
    };

    // A pointer cursor over a draggable point, so it looks like what it is.
    const onEnter = () => {
      if (!dragging) map.getCanvas().style.cursor = 'grab';
    };
    const onLeave = () => {
      if (!dragging) map.getCanvas().style.cursor = '';
    };

    map.on('mousedown', DRAFT_POINT_LAYER, onDown);
    map.on('mousemove', onMove);
    map.on('mouseup', onUp);
    map.on('mouseenter', DRAFT_POINT_LAYER, onEnter);
    map.on('mouseleave', DRAFT_POINT_LAYER, onLeave);
    return () => {
      map.off('mousedown', DRAFT_POINT_LAYER, onDown);
      map.off('mousemove', onMove);
      map.off('mouseup', onUp);
      map.off('mouseenter', DRAFT_POINT_LAYER, onEnter);
      map.off('mouseleave', DRAFT_POINT_LAYER, onLeave);
    };
  }, [controller]);

  // Drag a simulator across the map (ctrl/⌘-drag moves the whole selection).
  useSimDrag(controller, onMessage);

  // Reshape or move the selected zone.
  //
  // The shape changes locally on every mousemove and is written once, on
  // release. The predecessor wrote the whole list on every mousemove, about
  // sixty times a second; the writes landed out of order, the server kept a
  // mid-drag shape, and the next read snapped the zone to a size nobody chose.
  // The one write is conditional on the etag, so a zone somebody else drew
  // while the mouse was down is not deleted by a list that predates it.
  //
  // A press that lands on the selected zone also swallows the click that
  // follows, so letting go of a corner never drops a waypoint under it.
  const suppressClickRef = useRef(false);
  useEffect(
    () =>
      controller.ready(() => {
        const map = controller.raw();
        if (!map) return undefined;

        let drag: {
          origin: Zone;
          role: HandleRole | 'body';
          index: number;
          startLat: number;
          startLon: number;
          moved: boolean;
        } | null = null;

        const hits = (point: maplibregl.Point, layer: string) =>
          map.getLayer(layer) ? map.queryRenderedFeatures(point, { layers: [layer] }) : [];

        const onDown = (event: maplibregl.MapMouseEvent) => {
          suppressClickRef.current = false;
          const zoneId = selectedZoneRef.current;
          if (!zoneId || zoneDrawRef.current) return;
          const zone = useMissionStore.getState().zones.find((z) => z.zoneId === zoneId);
          if (!zone) return;
          // A waypoint on top of the zone is the waypoint drag's to move.
          if (hits(event.point, DRAFT_POINT_LAYER).length) return;

          const { lat, lng } = event.lngLat;
          const handle = hits(event.point, ZONE_HANDLE_LAYER).find(
            (feature) => feature.properties?.zoneId === zoneId,
          );
          let role: HandleRole | 'body';
          let index = 0;
          if (handle && handle.properties.role !== 'centre') {
            role = handle.properties.role as HandleRole;
            index = Number(handle.properties.index);
          } else if (handle || pointInZone(lat, lng, zone)) {
            role = 'body';
          } else {
            return;
          }
          drag = { origin: zone, role, index, startLat: lat, startLon: lng, moved: false };
          suppressClickRef.current = true;
          event.preventDefault();
          map.dragPan.disable();
          map.getCanvas().style.cursor = 'grabbing';
        };

        const onMove = (event: maplibregl.MapMouseEvent) => {
          if (!drag) return;
          drag.moved = true;
          const { lat, lng } = event.lngLat;
          const next =
            drag.role === 'corner'
              ? moveCorner(drag.origin, drag.index, lat, lng)
              : drag.role === 'radius'
                ? resizeCircleTo(drag.origin, lat, lng)
                : translateZone(drag.origin, lat - drag.startLat, lng - drag.startLon);
          missionActions.previewZone(next);
        };

        const onUp = () => {
          if (!drag) return;
          const finished = drag;
          drag = null;
          map.dragPan.enable();
          map.getCanvas().style.cursor = '';
          if (!finished.moved) return;
          void missionActions.commitZones().then((result) => {
            if (!result.ok) onMessageRef.current(result.message, true);
          });
        };

        map.on('mousedown', onDown);
        map.on('mousemove', onMove);
        map.on('mouseup', onUp);
        // Released off the map, the canvas never hears the mouseup, and the
        // drag would carry on with the next mouse movement.
        window.addEventListener('mouseup', onUp);
        return () => {
          map.off('mousedown', onDown);
          map.off('mousemove', onMove);
          map.off('mouseup', onUp);
          window.removeEventListener('mouseup', onUp);
        };
      }),
    [controller],
  );

  // Double-click finishes a polygon, and only while one is being drawn. Its
  // first click has already placed the last corner and its second was ignored
  // as a duplicate (polygonClick). Otherwise double-click stays what it was:
  // two waypoint clicks, with zoom off (MapCanvas).
  useEffect(
    () =>
      controller.ready(() => {
        const map = controller.raw();
        if (!map) return undefined;
        const onDouble = (event: maplibregl.MapMouseEvent) => {
          if (zoneDrawRef.current?.shape !== 'polygon') return;
          event.preventDefault();
          finishPolygonRef.current();
        };
        map.on('dblclick', onDouble);
        return () => {
          map.off('dblclick', onDouble);
        };
      }),
    [controller],
  );

  // Escape lets go of whatever is being drawn or edited.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (zoneDrawRef.current) setZoneDraw(null);
      else if (selectedZoneRef.current) setSelectedZoneId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Bring the picked aircraft into view when the operator selects one. The
  // roster lists the whole fleet with no AOR filter, so a drone can sit outside
  // the current view — in the list but "missing" from the map. The first render
  // is skipped so opening Planning keeps Command's inherited camera rather than
  // snapping to whatever was already selected.
  const flyToMounted = useRef(false);
  useEffect(() => {
    if (!flyToMounted.current) {
      flyToMounted.current = true;
      return;
    }
    if (!editingDrone) return;
    const drone = useDroneStore.getState().drones.get(editingDrone);
    const lat = drone?.scalars.lat;
    const lon = drone?.scalars.lon;
    if (typeof lat !== 'number' || typeof lon !== 'number') return;
    if (lat === 0 && lon === 0) return;
    controller.flyTo(lat, lon);
  }, [editingDrone, controller]);

  // Delete / Backspace removes the selected waypoint, the way a map editor is
  // expected to behave. Ignored while typing into a field, so editing a plan
  // name never eats a keystroke as a delete.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if (selectedWp === null || !editingDrone) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
      event.preventDefault();
      missionActions.removeWaypoint(editingDrone, selectedWp);
      setSelectedWp(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedWp, editingDrone]);

  // Left-click drops the active palette tool at the point.
  useEffect(
    () =>
      controller.onClick((point) => {
        // The press that started a zone drag, or grabbed the selected zone
        // without moving it, is not also a waypoint.
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          return;
        }
        // Drawing a zone owns the click while it is armed, so a stray waypoint
        // is never dropped in the middle of defining a boundary.
        const draw = zoneDrawRef.current;
        if (draw) {
          addZonePoint(point.lat, point.lon);
          return;
        }
        // Placing a target owns the click the same way a zone draw does, so
        // arming the targeting planner never also drops a waypoint.
        if (targetingActions.isPlacing()) {
          targetingActions.place(point.lat, point.lon);
          onMessage(`Target set · ${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`);
          return;
        }
        // Zoomed out, a click inside an AOR box means "take me there", not
        // "drop a waypoint" — nobody plans a route at globe zoom, and the boxes
        // are the only thing on screen worth pointing at.
        if (useMapStore.getState().lod !== 'tactical') {
          const map = controller.raw();
          const hit = map
            ?.queryRenderedFeatures(map.project([point.lon, point.lat]), {
              layers: ['aor-fill'],
            })
            .find((feature) => typeof feature.properties?.aorId === 'string');
          const aorId = hit?.properties?.aorId;
          if (typeof aorId === 'string') {
            const target = aorById(aorId);
            // Make the AOR the active one, not just fly the camera there. The
            // top-left label, the "LIVE N AC" count, and where a spawned sim
            // lands all read workspace.aor — without this the map jumps to the
            // box but the whole screen stays scoped to the AOR you left.
            useWorkspaceStore.getState().setAor(aorId);
            controller.flyTo(target.center[1], target.center[0], target.zoom);
            onMessage(`AOR ${aorId}`);
            return;
          }
        }
        const target = missionActions.editingDroneId();
        if (!target) return;
        missionActions.appendWaypoint(target, point.lat, point.lon);
        const cmd = toolRef.current;
        if (cmd !== MavCmd.NavWaypoint) {
          const d = missionActions.getDraft(target);
          missionActions.updateWaypoint(target, d.length - 1, { command: cmd });
        }
      }),
    // addZonePoint reads its state through a ref, so the handler is registered
    // once and never torn down mid-draw.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controller],
  );

  // Terrain-sampled analysis; recomputes when the route or terrain state change.
  const analysis = useMemo(() => {
    const map = controller.raw();
    const groundAt = (lat: number, lon: number): number | null => {
      if (!map || typeof map.queryTerrainElevation !== 'function') return null;
      const e = map.queryTerrainElevation([lon, lat]);
      return typeof e === 'number' && Number.isFinite(e) ? e : null;
    };
    return analyze(draft, groundAt);
    // terrainOn gates whether queryTerrainElevation returns anything.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller, draft, terrainOn]);

  const buildMenu: MenuBuilder = (point) => {
    const target = missionActions.editingDroneId();
    const coord = `${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`;
    const copyItem: MenuItem = {
      label: 'Copy coordinates',
      sub: coord,
      onSelect: () => void navigator.clipboard?.writeText(`${point.lat.toFixed(6)}, ${point.lon.toFixed(6)}`),
    };
    // Zones are not tied to an aircraft, so they are offered whether or not one
    // is selected. A dropped zone is a 500 m circle the operator can see and
    // remove. Saving it writes to no aircraft; the fence panel pushes it.
    const addZone = (kind: 'keep-in' | 'keep-out') => {
      missionActions.addZone({
        name: kind === 'keep-in' ? 'Keep-in' : 'Keep-out',
        kind,
        shape: { type: 'circle', lat: point.lat, lon: point.lon, radiusM: ZONE_DEFAULT_RADIUS_M },
      });
      onMessage(`${kind === 'keep-in' ? 'Keep-in' : 'Keep-out'} zone added. Aircraft enforce it once it is pushed to them.`);
    };
    const zoneItems: MenuItem[] = [
      { label: 'Add keep-in zone', sub: `stay inside · ${ZONE_DEFAULT_RADIUS_M} m`, onSelect: () => addZone('keep-in') },
      { label: 'Add keep-out zone', sub: `stay clear · ${ZONE_DEFAULT_RADIUS_M} m`, onSelect: () => addZone('keep-out') },
    ];
    if (!target)
      return [
        { label: 'Select an aircraft to plan for', disabled: true },
        { label: '', separator: true },
        ...zoneItems,
        { label: '', separator: true },
        copyItem,
      ];
    const insert = (command: MavCmdValue) => {
      missionActions.appendWaypoint(target, point.lat, point.lon);
      if (command !== MavCmd.NavWaypoint) {
        const d = missionActions.getDraft(target);
        missionActions.updateWaypoint(target, d.length - 1, { command });
      }
    };
    const d = missionActions.getDraft(target);
    const items: MenuItem[] = [
      { label: 'Add waypoint', sub: coord, onSelect: () => insert(MavCmd.NavWaypoint) },
      { label: 'Add takeoff', sub: coord, onSelect: () => insert(MavCmd.NavTakeoff) },
      { label: 'Add loiter (hold)', sub: coord, onSelect: () => insert(MavCmd.NavLoiterUnlim) },
      { label: 'Add ROI (look at)', sub: coord, onSelect: () => insert(MavCmd.DoSetRoi) },
      { label: 'Add landing', sub: coord, onSelect: () => insert(MavCmd.NavLand) },
      { label: 'Add RTL', sub: 'return to launch', onSelect: () => insert(MavCmd.NavReturnToLaunch) },
    ];
    if (d.length) {
      let nearest = 0;
      let best = Infinity;
      d.forEach((wp, index) => {
        const dist = haversineM(point.lat, point.lon, wp.lat, wp.lon);
        if (dist < best) {
          best = dist;
          nearest = index;
        }
      });
      items.push({ label: '', separator: true });
      items.push({
        label: `Move waypoint ${nearest + 1} here`,
        sub: `nearest · ${(best / 1000).toFixed(2)} km away`,
        onSelect: () => missionActions.moveWaypoint(target, nearest, point.lat, point.lon),
      });
      items.push({
        label: 'Clear draft',
        sub: `${d.length} waypoint${d.length === 1 ? '' : 's'}`,
        danger: true,
        onSelect: () => missionActions.clearDraft(target),
      });
    }
    items.push({ label: '', separator: true });
    items.push(...zoneItems);
    items.push({ label: '', separator: true });
    items.push(copyItem);
    return items;
  };

  return (
    <>
      <MapContextMenu build={buildMenu} />
      <PlanPanel
        editingDrone={editingDrone}
        analysis={analysis}
        selectedWp={selectedWp}
        onSelectWp={(i) => setSelectedWp((current) => (current === i ? null : i))}
        onUpload={() => setUploadOpen(true)}
        onMessage={onMessage}
        message={message}
      />
      <RoutePanel
        editingDrone={editingDrone}
        analysis={analysis}
        selectedWp={selectedWp}
        onMessage={onMessage}
        zoneDraw={zoneDraw}
        onZoneDraw={startZoneDraw}
        selectedZoneId={selectedZoneId}
        onSelectZone={setSelectedZoneId}
        open={routeOpen}
      />
      {/* Tablet: the route panel is a drawer. Beside the waypoint list and the
          two docks there is no room for a third 324px column at 768-1279. */}
      {tier === 'tablet' ? (
        <button
          type="button"
          aria-expanded={routeOpen}
          onClick={toggleRoute}
          className="glass-pill hit-44 pointer-events-auto absolute right-[14px] top-[86px] z-20 rounded-full px-3 py-2 font-mono text-[9px] font-bold tracking-wide text-white/70"
        >
          {routeOpen ? 'ROUTE ▸' : '◂ ROUTE'}
        </button>
      ) : null}
      {/* The AI operator sits beside the route panel, not on its own screen: a
          proposal is reviewed against the same map and the same draft it would
          replace. Collapsed by default so it never takes the map from an
          operator who did not ask for it. */}
      <CopilotDock />
      {/* Targeting planner: place a target, plan a dive or a drop, and see the
          geometry on the map. Collapsed by default; it drafts a route, it never
          flies one. */}
      <AttackDock />
      <AttackHoverLabel />
      <ExternalTrackHoverLabel />
      <PaletteDock tool={tool} onTool={setTool} />
      {zoneDraw ? (
        <ZoneDrawDock
          draw={zoneDraw}
          onDone={finishPolygon}
          onCancel={() => setZoneDraw(null)}
          onUndo={() =>
            setZoneDraw((d) => (d && d.points.length ? { ...d, points: d.points.slice(0, -1) } : d))
          }
        />
      ) : null}
      <ElevationProfile editingDrone={editingDrone} analysis={analysis} />
      {uploadOpen && editingDrone && identity ? (
        <UploadDialog
          editingDrone={editingDrone}
          simulated={identity.simulated}
          analysis={analysis}
          onClose={() => setUploadOpen(false)}
          onMessage={onMessage}
        />
      ) : null}
    </>
  );
}

/**
 * The camera Planning opens on.
 *
 * It inherits the view Command last settled on so planning happens on the same
 * ground the operator was flying — but ONLY when that view is tactical. If
 * Command was left on the strategic globe or a zoomed-out regional view,
 * planning there is useless: aircraft are dots too small to see, and a spawn
 * placed "where you are looking" (the map centre) lands at the globe's centre,
 * nowhere near the AOR. That is the "I go to Planning, it's on the globe, and I
 * spawn drones with no icons" report. Fall back to the AOR at planning zoom.
 */
export function planningInitialCamera(shared: MapCamera | null, aor: Aor): MapCamera {
  const aorView: MapCamera = {
    center: { lat: aor.center[1], lon: aor.center[0] },
    zoom: 11,
    pitch: 0,
    bearing: 0,
  };
  if (!shared || lodForZoom(shared.zoom) !== 'tactical') return aorView;
  return shared;
}

export function PlanningView(): ReactNode {
  const aor = aorById(useWorkspaceStore((state) => state.aor));
  // Captured once at mount; the map seeds from props and then owns its camera.
  const [initial] = useState<MapCamera>(() =>
    planningInitialCamera(useMapStore.getState().camera, aor),
  );
  // Docks start closed each time Planning opens, as they did when each kept its own flag.
  useEffect(() => () => usePlanDocks.getState().reset(), []);
  return (
    <MapCanvas
      center={initial.center}
      zoom={initial.zoom}
      pitch={initial.pitch}
      bearing={initial.bearing}
    >
      <PlanningWorkspace />
    </MapCanvas>
  );
}

export default PlanningView;
